// The call page: parent details → get ready → the phone rings → live call with Santa → certificate.
import { GoogleGenAI, Modality, type LiveServerMessage, type Session } from '@google/genai';
import { guessCountry } from '../shared/countries.ts';
import { ELF_TAPS, ELF_WRAP_UP, MISSIONS, elfQuietNudge, pickMission, planElfCall } from '../shared/elf.ts';
import { EVENTS, NOT_YET, SANTA_WRAP_UP, SAYS_GOODBYE, SONG_CUE, TAPS, planCall, quietNudge, type CallPlan, type EventId, type SegmentId } from '../shared/script.ts';
import { accountBar, confirmCheckout, me, offersCard, signInCard, wireAccount, type Me } from './account.ts';
import { startCabin } from './cabin.ts';
import { drawCertificate } from './certificate.ts';
import { elfName } from './elfname.ts';
import { startPainted, startSwitchboard } from './painted.ts';
import { ambience, ambienceElf, audio, ring } from './sound.ts';
import { startVillage } from './village.ts';

const T = JSON.parse(document.getElementById('t')!.textContent!);
const C = T.call;
/** Santa's call, or the short elf call with Pipa at the switchboard. */
const ELF = document.body.classList.contains('elf');
const E = T.elfCall;
const $ = <E extends HTMLElement>(sel: string, root: ParentNode = document) => root.querySelector<E>(sel)!;
const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

startVillage($<HTMLCanvasElement>('#village'), { interactive: false, header: true });

type Step = 'form' | 'ready' | 'ringing' | 'call' | 'after';
function show(step: Step) {
  document.querySelectorAll<HTMLElement>('[data-step]').forEach((el) => (el.hidden = el.dataset.step !== step));
  document.body.dataset.phase = step;
  scrollTo({ top: 0, behavior: 'smooth' });
}

let details: Record<string, unknown> = {};
let plan: CallPlan = planCall();
let mic: MediaStream | null = null;
let clips: { intro: Promise<AudioBuffer | null>; outro: Promise<AudioBuffer | null> } | null = null;

// Who answers the phone and Mrs. Claus's goodbye are short pre-made clips; fetch them while the
// parent reads the tips. A clip that fails just gets skipped.
function fetchClip(kind: 'intro' | 'outro'): Promise<AudioBuffer | null> {
  return fetch('/api/voice', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ ...details, kind }) })
    .then((r) => (r.ok ? r.arrayBuffer() : null))
    .then((b) => (b ? audio().ctx.decodeAudioData(b) : null))
    .catch(() => null);
}

// Clips play through their own analyser so the cabin knows when Mrs. Claus (or Pipa) is talking.
let clipAnalyser: AnalyserNode | null = null;
function playClip(buf: AudioBuffer): Promise<void> {
  const { ctx, out } = audio();
  if (!clipAnalyser) {
    clipAnalyser = ctx.createAnalyser();
    clipAnalyser.fftSize = 512;
    clipAnalyser.connect(out);
  }
  const src = ctx.createBufferSource();
  src.buffer = buf;
  src.connect(clipAnalyser);
  src.start();
  // Also finish on time if the sound can't play (e.g. the browser blocked audio), so nothing hangs.
  return new Promise((done) => {
    src.onended = () => done();
    setTimeout(done, (buf.duration + 1) * 1000);
  });
}
function level(a: AnalyserNode | null) {
  if (!a) return 0;
  const data = new Uint8Array(a.fftSize);
  a.getByteTimeDomainData(data);
  let sum = 0;
  for (const v of data) sum += (v - 128) ** 2;
  return Math.min(1, Math.sqrt(sum / data.length) / 30);
}
const withTimeout = <T>(p: Promise<T>, ms: number, fallback: T) => Promise.race([p, new Promise<T>((r) => setTimeout(() => r(fallback), ms))]);

// ---------- 1. details ----------
const form = $<HTMLFormElement>('#details');
// Pre-select the country from the browser's time zone and language; the parent can change it.
// Wishes posted in the home page's mailbox come along (this visit only).
try {
  const w = sessionStorage.getItem('santa-wishes');
  if (w) $<HTMLInputElement>('#wishes').value = w;
} catch {}
$<HTMLSelectElement>('#country').value = guessCountry(Intl.DateTimeFormat().resolvedOptions().timeZone, navigator.languages ?? [navigator.language]);

/** The caller's own calendar and clock, so Santa knows if it's morning or bedtime there. */
function localNow() {
  const n = new Date();
  return { y: n.getFullYear(), m: n.getMonth() + 1, d: n.getDate(), hour: n.getHours(), minute: n.getMinutes(), weekday: n.toLocaleDateString('en-GB', { weekday: 'long' }) };
}
form.addEventListener('submit', (e) => {
  e.preventDefault();
  // The elf form is shorter than Santa's: a field that isn't there is just empty.
  const v = (id: string) => (document.querySelector<HTMLInputElement>('#' + id)?.value || '').trim();
  const err = $('.error', form);
  if (!v('name') || !$<HTMLInputElement>('#consent').checked) {
    err.hidden = false;
    err.textContent = !v('name') ? `${form.querySelector('label span')!.textContent}?` : $('.consent span').textContent!;
    (v('name') ? $('#consent') : $('#name')).focus();
    return;
  }
  err.hidden = true;
  details = { name: v('name'), age: Number(v('age')), country: v('country'), town: v('town'), local: localNow(), wishes: v('wishes'), proud: v('proud'), people: v('people'), avoid: v('avoid'), consent: true, lang: T.lang };
  const voice = new URLSearchParams(location.search).get('voice');
  if (voice) details.voice = voice;
  // A fresh random plan each call (dev: ?opening=nap forces an opening).
  plan = planCall();
  const forced = new URLSearchParams(location.search).get('opening');
  if (forced) plan.opening = forced as CallPlan['opening'];
  Object.assign(details, plan);
  if (ELF) {
    // Pipa remembers (on this device only) the sparkle mission she gave last time.
    const last = elfMemory();
    const lastMission = last && last.name.toLowerCase() === String(details.name).toLowerCase() ? last.mission : undefined;
    const n = new Date();
    const elfPlan = planElfCall();
    Object.assign(details, {
      elf: elfPlan,
      kind: 'elf',
      mission: pickMission(lastMission),
      lastMission,
      advent: n.getMonth() === 11 && n.getDate() <= 24 ? T.advent?.doors?.[n.getDate() - 1]?.text : undefined,
    });
    plan.events = [];
    plan.goodbye = elfPlan.goodbye;
  } else clips = { intro: fetchClip('intro'), outro: fetchClip('outro') };
  renderReady();
  show('ready');
});

// ---------- 2. get ready ----------
function renderReady() {
  const el = $('[data-step="ready"]');
  el.innerHTML = `<h1>${esc(C.ready.title)}</h1>
    <ul class="tips">${C.ready.tips.map((t: string) => `<li>${esc(t)}</li>`).join('')}</ul>
    <label class="consent watch"><input type="checkbox" data-watch> <span><strong>${esc(C.ready.watch)}</strong><br><small>${esc(C.ready.watchNote)}</small></span></label>
    <button class="btn btn-call" type="button" data-mic>${esc(C.ready.micButton)}</button>
    <p class="error" role="alert" hidden></p>`;
  $('[data-mic]', el).addEventListener('click', async () => {
    // iOS: a call both plays and records, and must still be heard with the silent switch on.
    const nav = navigator as any;
    if (nav.audioSession) nav.audioSession.type = 'play-and-record';
    audio(); // unlock sound on this tap
    try {
      mic = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      watchWanted = $<HTMLInputElement>('[data-watch]', el).checked;
      startRinging();
    } catch {
      const err = $('.error', el);
      err.hidden = false;
      err.textContent = C.ready.micDenied;
    }
  });
}

// ---------- 3. ringing ----------
let stopRing: (() => void) | null = null;
let watchWanted = false;
function startRinging() {
  const el = $('[data-step="ringing"]');
  el.innerHTML = `<div class="ringing">
      <div class="old-phone" aria-hidden="true">${PHONE_SVG}</div>
      <p class="calling">${esc(ELF ? E.calling : C.ringing.calling)}</p>
      <button class="answer" type="button" aria-label="${esc(C.ringing.answer)}">${HANDSET}<span>${esc(C.ringing.answer)}</span></button>
    </div>`;
  show('ringing');
  stopRing = ring();
  $('.answer', el).addEventListener('click', () => {
    stopRing?.();
    void startCall();
  });
}

// ---------- 4. the call ----------
async function startCall() {
  const el = $('[data-step="call"]');
  const answerer = ELF ? E.who : plan.opening === 'elf' ? C.inCall.elf : C.inCall.mrsClaus;
  el.innerHTML = `<div class="santa-room">
      <canvas class="room" aria-hidden="true"></canvas>
      <div class="call-bar">
        <span class="who">${esc(answerer)}</span>
        <span class="status"></span>
        <span class="clock" hidden></span>
      </div>
      <p class="captions" hidden></p>
      <div class="call-controls">
        <button type="button" class="ctl" data-captions aria-pressed="false">${esc(C.inCall.captions)}</button>
        <button type="button" class="ctl" data-mute aria-pressed="false">${esc(C.inCall.mute)}</button>
        <button type="button" class="hangup" data-hangup aria-label="${esc(C.inCall.hangUp)}">${HANDSET}</button>
      </div>
      <p class="error" role="alert" hidden></p>
    </div>`;
  show('call');
  const who = $('.who', el);
  const status = $('.status', el);
  const clock = $('.clock', el);
  const captions = $('.captions', el);
  const errorBox = $('.error', el);
  let call: LiveCall | null = null;
  let over = false;
  const stopAmbience = ELF ? ambienceElf() : ambience();
  const roomCanvas = $<HTMLCanvasElement>('canvas.room', el);
  const roomMood = () => ({ ...(call?.mood() ?? { santa: 0, child: 0 }), other: level(clipAnalyser) });
  // The painted cabin; the drawn one is the fallback if the paintings can't load.
  const cabin: { stop(): void; phase(p: 'intro' | 'call' | 'outro'): void; event(id: EventId): void; said?(text: string): void } = await (ELF
    ? startSwitchboard(roomCanvas, roomMood, { name: String(details.name || '') })
    : startPainted(roomCanvas, roomMood, { opening: plan.opening, name: String(details.name || '') }).catch(() => startCabin(roomCanvas, roomMood, plan)));
  // Optional: Santa's eyes follow the child's face (camera stays on the device).
  let stopWatch: (() => void) | null = null;
  if (watchWanted && 'viewer' in cabin)
    void import('./watch.ts')
      .then(({ watchViewer }) => watchViewer((v) => (cabin as any).viewer(v)))
      .then((stop) => (over ? stop() : (stopWatch = stop)))
      .catch((e) => console.warn('watch off', e));
  const finish = () => {
    if (over) return;
    over = true;
    stopWatch?.();
    stopAmbience();
    cabin.stop();
    showAfter();
  };
  const fail = (key: string) => {
    errorBox.hidden = false;
    errorBox.textContent = C.errors[key] || C.errors.generic;
    status.textContent = '';
  };
  // The red button: with time left, Santa (or Pipa) says a warm goodbye first; a second press hangs up at once.
  $('[data-hangup]', el).addEventListener('click', () => {
    if (!call) return finish();
    if (call.leave()) status.textContent = C.inCall.leaving;
  });
  $('[data-mute]', el).addEventListener('click', (e) => {
    const b = e.currentTarget as HTMLButtonElement;
    const muted = call?.toggleMute() ?? false;
    b.setAttribute('aria-pressed', String(muted));
    b.textContent = muted ? C.inCall.unmute : C.inCall.mute;
  });
  $('[data-captions]', el).addEventListener('click', (e) => {
    const b = e.currentTarget as HTMLButtonElement;
    const on = b.getAttribute('aria-pressed') !== 'true';
    b.setAttribute('aria-pressed', String(on));
    captions.hidden = !on;
  });

  // Santa's line connects quietly while Mrs. Claus (or Pipa) answers.
  const connecting = (async () => {
    const res = await fetch('/api/call', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(details) }).catch(() => null);
    const data = res ? await res.json().catch(() => ({})) : {};
    if (!res?.ok) throw new Error(ACCOUNT_ERRORS[data.error] ? `account:${data.error}` : data.error || 'generic');
    const c = new LiveCall(data, plan);
    await c.connect();
    return c;
  })();
  connecting.catch(() => {});

  const intro = ELF ? null : await withTimeout(clips?.intro ?? Promise.resolve(null), 8000, null);
  if (intro) await playClip(intro);
  try {
    call = await connecting;
  } catch (e: any) {
    const msg = String(e?.message || '');
    if (msg.startsWith('account:')) {
      errorBox.hidden = false;
      errorBox.textContent = ACCOUNT_ERRORS[msg.slice(8)];
      return;
    }
    return fail(msg);
  }
  if (over) return call.end('hangup');

  // The handover: Santa takes the phone.
  cabin.phase('call');
  who.textContent = ELF ? E.who : C.inCall.santa;
  call.onClock = (left) => {
    clock.hidden = false;
    clock.textContent = `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')} ${C.inCall.timeLeft}`;
  };
  call.onCaption = (text) => (captions.textContent = text);
  call.onEvent = (id) => cabin.event(id);
  // The child's taps in the cabin: Santa hears about them (in a quiet moment, not too often).
  (cabin as any).onBeat?.((id: string, source: string) => {
    const taps: Record<string, string> = ELF ? ELF_TAPS : TAPS;
    if (source === 'tap' && taps[id]) call?.notice(`[ELVES: ${taps[id]} If it fits, react to it in one short, playful sentence, then carry on.]`);
  });
  call.onSaid = (text) => cabin.said?.(text);
  call.onError = () => fail('generic');
  call.onEnd = async (reason) => {
    if (reason === 'done' && !ELF) {
      // Mrs. Claus pops in to wave goodbye.
      const outro = await withTimeout(clips?.outro ?? Promise.resolve(null), 1500, null);
      if (outro) {
        cabin.phase('outro');
        await new Promise((r) => setTimeout(r, 1800));
        await playClip(outro);
        await new Promise((r) => setTimeout(r, 900));
      }
    }
    finish();
  };
  call.begin();
}

class LiveCall {
  onStatus = (_s: string) => {};
  onClock = (_left: number) => {};
  onCaption = (_t: string) => {};
  onSaid = (_chunk: string) => {};
  onError = () => {};
  onEnd = (_reason: 'done' | 'hangup') => {};
  onEvent = (_id: EventId) => {};

  private session: Session | null = null;
  private micCtx: AudioContext | null = null;
  private muted = false;
  private ended = false;
  private playing: AudioBufferSourceNode[] = [];
  private nextAt = 0;
  private analyser: AnalyserNode;
  private out: GainNode;
  private startedAt = 0;
  private timer = 0;
  private said = '';
  private wrapUpSent = false;
  private goodbyeSent = false;
  private childLevel = 0;
  private quietSince = 0;
  // When the child last said real words (Google's transcript of their voice). The mic level alone
  // can't tell speech from a humming room once the browser's auto-gain lifts the background.
  private heardAt = -99;
  private pendingNotice: string | null = null;
  private usage = { inText: 0, inAudio: 0, outText: 0, outAudio: 0 };
  private turnText = '';
  private lastNotice = -99;
  private eventsAt: { id: EventId; at: number }[];
  // Leading the show: when the child goes quiet, the elves nudge Santa (or Pipa) on to the next part.
  private inTurn = false;
  private childAt = 0;
  private lastNudge = -99;
  private quietRun = 0;
  private showLeft: SegmentId[];
  private turnStartedAt = 0;
  private leavingAt = 0;

  constructor(private cfg: { token: string; model: string; seconds: number; callId?: number }, private plan: CallPlan) {
    // Two surprises: one early, one in the middle, each when nobody is talking.
    this.eventsAt = plan.events.map((id, i) => ({ id, at: (i === 0 ? 70 : 180) + Math.random() * 40 }));
    this.showLeft = [...(plan.show ?? [])];
    const { ctx, out } = audio();
    this.analyser = ctx.createAnalyser();
    this.analyser.fftSize = 512;
    this.out = ctx.createGain();
    this.out.gain.value = 1.4;
    this.out.connect(this.analyser);
    this.analyser.connect(out);
  }

  async connect() {
    const ai = new GoogleGenAI({ apiKey: this.cfg.token, httpOptions: { apiVersion: 'v1alpha' } });
    try {
      this.session = await ai.live.connect({
        model: this.cfg.model,
        config: { responseModalities: [Modality.AUDIO] },
        callbacks: {
          onmessage: (m) => this.message(m),
          onerror: (e) => {
            console.error('live error', e);
            if (!this.ended) this.onError();
          },
          onclose: (e) => {
            console.info('live closed', e.code, e.reason);
            if (!this.ended) this.finish(this.goodbyeSent ? 'done' : 'hangup');
          },
        },
      });
    } catch (e) {
      console.error(e);
      throw new Error('generic');
    }
    await this.startMic();
  }

  /** Santa takes the phone. */
  begin() {
    this.elves('[CALL CONNECTED] You have the phone now. Greet the child as described in how the call begins.');
    this.startedAt = performance.now();
    this.timer = window.setInterval(() => this.tick(), 250);
  }

  /** Stage directions from "the elves" (Santa's instructions explain them). */
  private elves(text: string) {
    try {
      this.session?.sendRealtimeInput({ text });
    } catch {
      this.session?.sendClientContent({ turns: [{ role: 'user', parts: [{ text }] }], turnComplete: true });
    }
  }

  private async startMic() {
    // The microphone runs in its own context at the device rate; the worklet downsamples to 16 kHz.
    this.micCtx = new AudioContext();
    await this.micCtx.audioWorklet.addModule('/mic-tap.js');
    const src = this.micCtx.createMediaStreamSource(mic!);
    const tap = new AudioWorkletNode(this.micCtx, 'mic-tap');
    tap.port.onmessage = (e) => {
      if (this.muted || this.ended || !this.session) return;
      const pcm = new Int16Array(e.data as ArrayBuffer);
      let sum = 0;
      for (let i = 0; i < pcm.length; i += 4) sum += pcm[i] * pcm[i];
      this.childLevel = Math.min(1, Math.sqrt(sum / (pcm.length / 4)) / 6000);
      const bytes = new Uint8Array(e.data as ArrayBuffer);
      let bin = '';
      for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
      this.session.sendRealtimeInput({ audio: { data: btoa(bin), mimeType: 'audio/pcm;rate=16000' } });
    };
    src.connect(tap);
  }

  private message(m: LiveServerMessage) {
    // Google's usage counts, for the cost log (never what was said)
    for (const d of m.usageMetadata?.promptTokensDetails ?? []) this.usage[d.modality === 'AUDIO' ? 'inAudio' : 'inText'] += d.tokenCount ?? 0;
    for (const d of m.usageMetadata?.responseTokensDetails ?? []) this.usage[d.modality === 'AUDIO' ? 'outAudio' : 'outText'] += d.tokenCount ?? 0;
    const sc = m.serverContent;
    if (!sc) return;
    if (sc.interrupted) this.flush();
    if (sc.inputTranscription?.text?.trim() && this.startedAt) this.heardAt = (performance.now() - this.startedAt) / 1000;
    for (const part of sc.modelTurn?.parts ?? []) {
      if (part.inlineData?.data) {
        if (!this.inTurn) this.turnStartedAt = performance.now();
        this.inTurn = true;
        this.play(part.inlineData.data);
      }
    }
    if (sc.outputTranscription?.text) {
      this.said += sc.outputTranscription.text;
      this.turnText += sc.outputTranscription.text;
      this.onCaption(this.said.slice(-160));
      this.onSaid(sc.outputTranscription.text);
    }
    if (sc.turnComplete) {
      this.inTurn = false;
      const turn = this.turnText;
      this.turnText = '';
      this.said = '';
      // A goodbye before the elves asked for it: near the end we take it as the real goodbye,
      // earlier the elves playfully tell them there's still time.
      if (!this.goodbyeSent && SAYS_GOODBYE.test(turn)) {
        if (this.wrapUpSent) this.goodbyeSent = true;
        else this.elves(NOT_YET);
      }
      // (a turn that was already under way when the child pressed the red button is not the goodbye)
      if (this.goodbyeSent && !(this.leavingAt && this.turnStartedAt < this.leavingAt)) {
        // Let the last words finish playing, then hang up.
        const { ctx } = audio();
        const wait = Math.max(0, this.nextAt - ctx.currentTime) * 1000 + 800;
        setTimeout(() => this.end('done'), wait);
      }
    }
  }

  private play(b64: string) {
    const { ctx } = audio();
    const bin = atob(b64);
    const n = bin.length >> 1;
    const buf = ctx.createBuffer(1, n, 24000);
    const ch = buf.getChannelData(0);
    for (let i = 0; i < n; i++) {
      const v = (bin.charCodeAt(i * 2) | (bin.charCodeAt(i * 2 + 1) << 8)) << 16 >> 16;
      ch[i] = v / 32768;
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(this.out);
    this.nextAt = Math.max(this.nextAt, ctx.currentTime + 0.06);
    src.start(this.nextAt);
    this.nextAt += buf.duration;
    this.playing.push(src);
    src.onended = () => (this.playing = this.playing.filter((s) => s !== src));
    this.onStatus('');
  }

  private flush() {
    for (const s of this.playing) s.stop();
    this.playing = [];
    this.nextAt = 0;
  }

  private tick() {
    const elapsed = (performance.now() - this.startedAt) / 1000;
    const left = Math.max(0, Math.round(this.cfg.seconds - elapsed));
    this.onClock(left);
    // Surprises happen in a quiet moment; Santa hears about it a beat after the child sees it.
    const m = this.mood();
    if (m.santa > 0.05 || this.playing.length || this.inTurn || elapsed - this.heardAt < 1.5) this.quietSince = elapsed;
    if (this.pendingNotice && !this.wrapUpSent && elapsed - this.quietSince > 0.8 && elapsed - this.lastNotice > 20) {
      this.elves(this.pendingNotice);
      this.pendingNotice = null;
      this.lastNotice = elapsed;
    }
    const due = this.eventsAt.find((e) => elapsed >= e.at);
    if (due && !this.wrapUpSent && elapsed - this.quietSince > 1.2) {
      this.eventsAt = this.eventsAt.filter((e) => e !== due);
      this.onEvent(due.id);
      setTimeout(() => this.elves(`[ELVES: ${EVENTS[due.id]}]`), 1500);
      this.lastNudge = elapsed; // the surprise gives Santa something to talk about
    }
    this.lead(elapsed, m.child);
    if (left <= 60 && !this.wrapUpSent) {
      this.wrapUpSent = true;
      this.elves(ELF ? ELF_WRAP_UP : SANTA_WRAP_UP);
    }
    if (left <= 15 && !this.goodbyeSent) {
      this.goodbyeSent = true;
      this.elves(`[ELVES: Time is up. Say a warm goodbye now in one or two sentences, because ${this.plan.goodbye}.]`);
    }
    if (elapsed > this.cfg.seconds + 25) this.end('done');
  }

  /**
   * Keep the show going. Small children often say nothing for a while; a real Santa wouldn't sit
   * in silence, so after a few quiet seconds (nobody speaking, nothing playing, Santa not mid-turn)
   * the elves nudge him on to the next part of his show. A second quiet spell in a row asks for
   * something to listen to instead of another question.
   */
  private lead(elapsed: number, _child: number) {
    this.childAt = Math.max(this.childAt, this.heardAt);
    if (this.childAt > this.lastNudge) this.quietRun = 0;
    const quiet = elapsed - this.quietSince;
    const wait = this.quietRun ? 6 : 4.5;
    if (this.wrapUpSent || this.goodbyeSent || elapsed < 20 || this.inTurn || this.playing.length || this.pendingNotice) return;
    // the song is a highlight: if chatting has filled the call so far, cue it around the middle
    if (!ELF && elapsed > 170 && quiet > 2 && this.showLeft.includes('song')) {
      this.showLeft = this.showLeft.filter((s) => s !== 'song');
      this.elves(SONG_CUE);
      this.lastNudge = elapsed;
      return;
    }
    if (quiet < wait || elapsed - this.lastNudge < 9) return;
    const again = this.quietRun > 0;
    const name = String(details.name || '');
    if (ELF) this.elves(elfQuietNudge(name, again));
    else {
      const listen = this.showLeft.find((s) => s === 'song' || s.startsWith('story'));
      const next = (again && listen) || this.showLeft[0] || null;
      this.showLeft = this.showLeft.filter((s) => s !== next);
      this.elves(quietNudge(name, next, again));
    }
    this.quietRun++;
    this.lastNudge = elapsed;
  }

  /** How loud Santa and the child are right now (0..1), for the cabin's acting. */
  mood() {
    const data = new Uint8Array(this.analyser.fftSize);
    this.analyser.getByteTimeDomainData(data);
    let sum = 0;
    for (const v of data) sum += (v - 128) ** 2;
    return { santa: Math.min(1, Math.sqrt(sum / data.length) / 30), child: this.childLevel };
  }

  /** Something the child did in the cabin; Santa hears it at the next quiet moment. */
  notice(text: string) {
    if (!this.ended && !this.goodbyeSent) this.pendingNotice = text;
  }

  toggleMute() {
    this.muted = !this.muted;
    mic?.getAudioTracks().forEach((t) => (t.enabled = !this.muted));
    return this.muted;
  }

  /**
   * The child pressed the red button. With time left and no goodbye yet, Santa (or Pipa) stops and
   * says a short, warm goodbye, then the call ends by itself. Returns false when it hung up at once
   * (a second press, or no goodbye arriving within 12 seconds).
   */
  leave(): boolean {
    if (this.ended) return false;
    if (this.goodbyeSent || !this.startedAt) {
      this.end('hangup');
      return false;
    }
    this.goodbyeSent = true;
    this.wrapUpSent = true;
    this.leavingAt = performance.now();
    this.flush();
    const name = String(details.name || '');
    this.elves(`[ELVES: ${name} wants to finish the call now: they pressed the red button. Do not try to keep them or ask anything. Say a warm, short goodbye in one or two sentences, because ${this.plan.goodbye}, and wish them a merry Christmas.]`);
    setTimeout(() => this.end('done'), 12000);
    return true;
  }

  end(reason: 'done' | 'hangup') {
    if (this.ended) return;
    this.finish(reason);
  }

  private finish(reason: 'done' | 'hangup' = 'hangup') {
    this.ended = true;
    // how long it lasted and what it used, for the cost log
    const seconds = this.startedAt ? Math.round((performance.now() - this.startedAt) / 1000) : 0;
    void fetch('/api/call/end', { method: 'POST', headers: { 'content-type': 'application/json' }, keepalive: true, body: JSON.stringify({ callId: this.cfg.callId, seconds, usage: this.usage, kind: ELF ? 'elf' : 'santa', reason }) }).catch(() => {});
    clearInterval(this.timer);
    this.flush();
    try {
      this.session?.close();
    } catch {}
    void this.micCtx?.close();
    mic?.getTracks().forEach((t) => t.stop());
    mic = null;
    this.onEnd(reason);
  }
}

// ---------- 5. after the call ----------
/** What Pipa remembers between calls; it stays on this device. */
function elfMemory(): { name: string; mission: string; date: string } | null {
  try {
    return JSON.parse(localStorage.getItem('pipa') || 'null');
  } catch {
    return null;
  }
}

/** After an elf call: the Nice List result, today's sparkle mission, and Santa for grown-ups. */
function showElfAfter() {
  const a = E.after;
  const name = String(details.name || '');
  const mission = String(details.mission || '');
  try {
    localStorage.setItem('pipa', JSON.stringify({ name, mission, date: new Date().toISOString().slice(0, 10) }));
  } catch {}
  const el = $('[data-step="after"]');
  el.innerHTML = `<div class="elf-after">
      <h1>${esc(a.title)}</h1>
      <p class="gold">★ ${esc(a.onList.replace('{name}', name))} ★</p>
      <div class="mission-card"><h3>${esc(a.mission)}</h3><p data-mission></p></div>
      <p>${esc(a.tomorrow)}</p>
      <p><a class="btn" href="${location.pathname}">${esc(a.again)}</a></p>
      <div class="santa-upsell"><p>${esc(a.santaNote)}</p><a class="btn btn-call" href="${T.santaPath}"><span class="ring">${HANDSET.replace('<svg', '<svg class="icon"')}</span>${esc(a.santa)}</a></div>
    </div>`;
  // The mission in the page's language (Pipa said it aloud in the child's language).
  const i = MISSIONS.indexOf(mission);
  $('[data-mission]', el).textContent = (E.missions?.[i] ?? mission.charAt(0).toUpperCase() + mission.slice(1)) + '!';
  show('after');
}

function showAfter() {
  if (ELF) return showElfAfter();
  const a = C.after;
  const name = String(details.name || '');
  const el = $('[data-step="after"]');
  el.innerHTML = `<h1>${esc(a.title)}</h1>
    <canvas class="cert-canvas" id="certificate" role="img" aria-label="${esc(a.certificate.replace('{name}', name))}"></canvas>
    <div class="after-actions">
      <button class="btn" type="button" data-save>${esc(a.save)}</button>
      <button class="btn btn-ghost" type="button" data-print>${esc(a.print)}</button>
      <a class="btn btn-ghost" href="${location.pathname}">${esc(a.again)}</a>
      <a class="btn btn-ghost" href="${document.querySelector<HTMLAnchorElement>('.logo')!.getAttribute('href')}">${esc(a.back)}</a>
    </div>`;
  show('after');
  const canvas = $<HTMLCanvasElement>('#certificate', el);
  const code = String(details.country || 'XX');
  const country = code === 'XX' ? '' : new Intl.DisplayNames([T.lang], { type: 'region' }).of(code) ?? '';
  const n = new Date();
  void drawCertificate(
    canvas,
    {
      name,
      age: Number(details.age) || 0,
      place: [details.town, country].filter(Boolean).join(', '),
      proud: String(details.proud || ''),
      wishes: String(details.wishes || ''),
      elf: elfName(name, T.elf).name,
      date: n.toLocaleDateString(T.lang, { day: 'numeric', month: 'long', year: 'numeric' }),
      number: String(1_000_000 + ((n.getTime() / 1000) % 9_000_000 | 0)),
    },
    a.cert,
  );
  $('[data-print]', el).addEventListener('click', () => print());
  $('[data-save]', el).addEventListener('click', () =>
    canvas.toBlob((blob) => {
      if (!blob) return;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${name || 'nice-list'}-nice-list.png`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 5000);
    }, 'image/png'),
  );
}

// ---------- artwork ----------
const HANDSET = `<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.25 11.4 11.4 0 0 0 3.6.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57a1 1 0 0 1-.25 1z"/></svg>`;

const PHONE_SVG = `<svg viewBox="0 0 220 200">
  <ellipse cx="110" cy="186" rx="90" ry="10" fill="rgba(0,0,0,.25)"/>
  <path d="M30 170 L50 90 Q110 70 170 90 L190 170 Z" fill="#c1121f"/>
  <path d="M50 90 Q110 70 170 90 L176 112 Q110 94 44 112 Z" fill="#e63946"/>
  <circle cx="110" cy="135" r="34" fill="#f1e3c8"/>
  <circle cx="110" cy="135" r="12" fill="#c1121f"/>
  ${Array.from({ length: 10 }, (_, i) => { const a = (i / 10) * 5.4 + 0.6; return `<circle cx="${110 + Math.cos(a) * 24}" cy="${135 + Math.sin(a) * 24}" r="5" fill="#c9b48a"/>`; }).join('')}
  <path d="M20 70 Q20 40 50 40 L170 40 Q200 40 200 70 L200 78 Q200 88 188 88 L170 88 Q160 88 160 76 L160 64 L60 64 L60 76 Q60 88 50 88 L32 88 Q20 88 20 78 Z" fill="#a4161a"/>
  <path d="M150 20 l10 -12 M110 14 l0 -14 M70 20 l-10 -12" stroke="#ffd166" stroke-width="5" stroke-linecap="round"/>
</svg>`;


// Dev preview of the cabin without a call: /call-santa?preview=cabin (Santa talks, then listens).
if (new URLSearchParams(location.search).get('preview') === 'cabin') {
  const el = $('[data-step="call"]');
  el.innerHTML = `<div class="santa-room"><canvas class="room" aria-hidden="true"></canvas></div>`;
  show('call');
  const t0 = performance.now();
  (window as any).__mood = null;
  const forced = new URLSearchParams(location.search).get('opening') as CallPlan['opening'] | null;
  startCabin($<HTMLCanvasElement>('canvas.room', el), () => {
    if ((window as any).__mood) return (window as any).__mood;
    const t = (performance.now() - t0) / 1000;
    const talking = t % 12 < 6;
    return { santa: talking ? 0.3 + 0.3 * Math.abs(Math.sin(t * 9)) : 0, child: talking ? 0 : 0.4 };
  }, forced ? { opening: forced } : undefined);
}

// Dev preview of the painted cabin: /call-santa?preview=painted (Santa talks, then listens).
if (new URLSearchParams(location.search).get('preview') === 'painted') {
  const el = $('[data-step="call"]');
  el.innerHTML = `<div class="santa-room"><canvas class="room" aria-hidden="true"></canvas></div>`;
  show('call');
  const t0 = performance.now();
  const opening = new URLSearchParams(location.search).get('opening') as CallPlan['opening'] | null;
  void import('./painted.ts').then(({ startPainted }) =>
    startPainted($<HTMLCanvasElement>('canvas.room', el), () => {
      if ((window as any).__mood) return (window as any).__mood;
      const t = (performance.now() - t0) / 1000;
      const talking = t % 10 < 5;
      return { santa: talking ? 0.25 + 0.3 * Math.abs(Math.sin(t * 9)) : 0, child: talking ? 0 : 0.4 };
    }, { opening: opening ?? undefined, name: 'Emma' }).then((ctl) => ((window as any).__ctl = ctl)),
  );
}

// Dev preview of the certificate: /call-santa?preview=certificate
if (new URLSearchParams(location.search).get('preview') === 'certificate') {
  Object.assign(details, { name: 'Emma', age: 5, country: 'FI', town: 'Helsinki', proud: 'learned to swim', wishes: 'a red bike, a dinosaur', mission: 'make someone laugh today' });
  showAfter();
}

// Dev preview of Pipa's switchboard: /call-santa?preview=elf
if (new URLSearchParams(location.search).get('preview') === 'elf') {
  const el = $('[data-step="call"]');
  el.innerHTML = `<div class="santa-room"><canvas class="room" aria-hidden="true"></canvas></div>`;
  show('call');
  const t0 = performance.now();
  void import('./painted.ts').then(({ startSwitchboard }) =>
    startSwitchboard($<HTMLCanvasElement>('canvas.room', el), () => {
      if ((window as any).__mood) return (window as any).__mood;
      const t = (performance.now() - t0) / 1000;
      const talking = t % 10 < 5;
      return { santa: talking ? 0.25 + 0.3 * Math.abs(Math.sin(t * 9)) : 0, child: talking ? 0 : 0.4 };
    }, { name: 'Emma' }).then((ctl) => ((window as any).__ctl = ctl)),
  );
}

// ---------- the grown-up side: sign-in, calls left, preview, buying ----------
const ACCOUNT_ERRORS: Record<string, string> = {
  signin: 'Please sign in first.',
  nocredit: 'There are no calls left on this account.',
  daily: "That's all the calls for today. Come back tomorrow!",
  blocked: 'This account is paused. Please contact us.',
  busy: 'The North Pole lines are very busy right now. Please try again a little later.',
};

const slot = document.querySelector<HTMLElement>('[data-account]');
async function showAccount() {
  if (!slot) return;
  const checkout = await confirmCheckout();
  let m: Me;
  try {
    m = await me();
  } catch {
    return;
  }
  const kind = ELF ? 'elf' : 'santa';
  const canCall = !!m.user && !m.user.blocked && (m.left?.[kind] ?? 0) > 0;
  // Local development without sign-in configured: just show the form.
  const devOpen = !m.signin && !m.devLogin;
  form.hidden = !(canCall || devOpen);
  slot.innerHTML = devOpen
    ? ''
    : !m.user
      ? signInCard(m)
      : accountBar(m) + (checkout === 'success' ? '<p class="thanks">Thank you! Your calls are ready.</p>' : '') + (canCall ? '' : offersCard(kind, m));
  slot.querySelector<HTMLFormElement>('[data-preview]')?.addEventListener('submit', (e) => {
    e.preventDefault();
    const name = String(new FormData(e.target as HTMLFormElement).get('name') || '').trim();
    if (name) void runPreview(kind, name);
  });
}
if (slot) {
  wireAccount(slot, () => void showAccount());
  void showAccount();
}

/** The free preview: pre-made lines played in the painted room, with all the acting. */
async function runPreview(kind: 'santa' | 'elf', name: string) {
  const ctx = audio().ctx; // unlock sound on this tap
  const r = await fetch('/api/preview', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ kind, name, lang: T.lang }) });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    const box = slot!.querySelector('.offers') ?? slot!;
    box.insertAdjacentHTML('beforeend', `<p class="error">${esc(ACCOUNT_ERRORS[data.error] || C.errors.generic)}</p>`);
    return;
  }
  const lines: { who: string; text: string; audio: string }[] = data.lines;
  const buffers = await Promise.all(lines.map((l) => ctx.decodeAudioData(Uint8Array.from(atob(l.audio), (ch) => ch.charCodeAt(0)).buffer)));
  const el = $('[data-step="call"]');
  el.innerHTML = `<div class="santa-room">
      <canvas class="room" aria-hidden="true"></canvas>
      <div class="call-bar"><span class="who">Preview</span></div>
      <p class="captions"></p>
      <div class="preview-end" hidden></div>
    </div>`;
  show('call');
  let speaker = '';
  const stopAmbience = kind === 'elf' ? ambienceElf() : ambience();
  const mood = () => ({ santa: speaker === 'santa' || speaker === 'elf' ? level(clipAnalyser) : 0, child: 0, other: speaker === 'mrsClaus' ? level(clipAnalyser) : 0 });
  const stage: any = kind === 'elf' ? await startSwitchboard($<HTMLCanvasElement>('canvas.room', el), mood, { name }) : await startPainted($<HTMLCanvasElement>('canvas.room', el), mood, { opening: 'fireside', name });
  const captions = $('.captions', el);
  for (let i = 0; i < lines.length; i++) {
    speaker = lines[i].who;
    if (speaker === 'santa') stage.phase('call');
    captions.textContent = lines[i].text;
    // feed the words in time with the voice, so the eyes, laughs and moments land on cue
    const sentences = lines[i].text.split(/(?<=[.!?…])\s+/);
    const total = sentences.reduce((n, s) => n + s.length, 0);
    let at = 0;
    for (const sentence of sentences) {
      setTimeout(() => stage.said?.(sentence), (at / total) * buffers[i].duration * 1000);
      at += sentence.length;
    }
    await playClip(buffers[i]);
  }
  speaker = '';
  const who = kind === 'elf' ? 'Pipa' : 'Santa';
  const minutes = kind === 'elf' ? 4 : 6;
  const end = $('.preview-end', el);
  end.hidden = false;
  end.innerHTML = `<h2>That was the preview!</h2>
    <p>In the real call, ${who} talks and listens to ${esc(name)} live for ${minutes} minutes${kind === 'elf' ? ', checks the Nice List properly and gives a secret sparkle mission' : ', knows what they wished for and what they did well this year'}.</p>
    <p><button class="btn btn-call" type="button" data-back>See the options</button></p>`;
  $('[data-back]', end).addEventListener('click', () => {
    stopAmbience();
    stage.stop?.();
    show('form');
    void showAccount();
  });
}
